import { App, View } from '@gin-betting/core';
import { CustomScoreTypesParser, MatchPartInfo } from 'types';
import { get } from 'lodash';

import { CONFIG_BY_SPORTS, SCORE_TYPES_TITLE_MAPPING } from 'scoreMetaConfig';

interface TextForScoreTitle {
    score: Pick<View.ScoreInfo, 'type' | 'indexNumber'>;
    currentMatchPart?: MatchPartInfo | null;
}

interface CustomMapping {
    score: Pick<View.ScoreInfo, 'type' | 'indexNumber'>;
    currentMatchPart?: MatchPartInfo | null;
}

const getCustomScoreTypesTitleMapping = ({
    currentMatchPart,
    score,
}: CustomMapping): CustomScoreTypesParser => {
    const isNumerable = App.ScoreTypesNumerable.includes(score.type);
    const scoreTypesTitleMapping = SCORE_TYPES_TITLE_MAPPING;

    if (!currentMatchPart?.sportId) {
        return {
            scoreTypesTitleMapping,
            isNumerable,
        };
    }

    const { sportId } = currentMatchPart;

    const config = get(CONFIG_BY_SPORTS, sportId, undefined);

    return (
        config?.setMatchPartTypes?.(score, currentMatchPart) ?? {
            scoreTypesTitleMapping,
            isNumerable,
        }
    );
};

const textForScoreTitle = ({
    currentMatchPart,
    score,
}: TextForScoreTitle): string => {
    const {
        scoreTypesTitleMapping,
        isNumerable,
    } = getCustomScoreTypesTitleMapping({
        score,
        currentMatchPart,
    });

    const scoreTitleText = scoreTypesTitleMapping[score.type] ?? '-';

    if (!isNumerable) {
        return scoreTitleText;
    }

    return customScoreTitle({
        index: score.indexNumber,
        sportId: currentMatchPart?.sportId,
        text: scoreTitleText,
    });
};

const getScoreTitle = (
    score: Pick<View.ScoreInfo, 'type' | 'indexNumber'>,
    currentMatchPart?: MatchPartInfo | null
): string => {
    if (!(score.type in SCORE_TYPES_TITLE_MAPPING)) {
        return score.type;
    }

    return textForScoreTitle({ currentMatchPart, score });
};

interface CustomScoreTitle {
    sportId: string | undefined;
    index: number;
    text: string;
}

const customScoreTitle = ({
    index,
    text,
    sportId,
}: CustomScoreTitle): string => {
    const title = index + text;

    if (!sportId) return title;

    const config = get(CONFIG_BY_SPORTS, sportId, undefined);

    return config?.titleParser?.(index, text) ?? title;
};

export default getScoreTitle;
